import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import 'react-resizable/css/styles.css';

import { Icon, ExpandIcon, AddIcon } from 'utils/icons';
import Avatar from 'components/Avatar';
import Button from 'components/Button';
import DropDownMenu from 'components/DropDownMenu';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import CornerSpinner from 'components/CornerSpinner';

import SwimlaneLayout from './SwimlaneLayout';
import { budgetMenu, userActivity } from 'utils/constants';
import { stopPropagation, toastStyle } from 'utils/helper';
import { useAppContext } from 'contexts/AppContext';
import FormInput from 'components/FormInput';
import API_ENDPOINTS from 'utils/api/endpoints';
import { useBudgetQueries } from 'utils/api/queries';
import { getPresenceColor } from 'utils/presence';
import { isInitialBudgetWorkspaceLoading } from 'utils/budgetTabTransitions';
import { buildMentionableUsers } from 'utils/commentMentions';

import './styles.scss';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import ALL_ROUTES from 'config/routes';
import { startSession } from 'store/slices/budgetSessionSlice';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import BudgetFooter from './BudgetFooter';

const PRESENCE_BREADCRUMB_POLL_INTERVAL_MS = 2000;
const CHANGE_EVENT_POLL_INTERVAL_MS = 5000;

const lockDragTransformToHorizontalAxis = transform => {
  if (!transform) return transform;

  const translate3dMatch = transform.match(
    /translate3d\(([-\d.]+)px,\s*[-\d.]+px,\s*([-\d.]+)px\)/
  );
  if (translate3dMatch) {
    return `translate3d(${translate3dMatch[1]}px, 0px, ${translate3dMatch[2]}px)`;
  }

  const translateMatch = transform.match(
    /translate\(([-\d.]+)px,\s*[-\d.]+px\)/
  );
  if (translateMatch) {
    return `translate(${translateMatch[1]}px, 0px)`;
  }

  return transform;
};

const getBudgetTabDraggableStyle = style => ({
  ...style,
  transform: lockDragTransformToHorizontalAxis(style?.transform),
});

const BudgetScreen = () => {
  const {
    budgetList,
    selectedBudget,
    contextualSuggestions,
    openTabsList,
    enteringBudgetId,
    openingCreatedBudgetId,
    showCreateBudgetModal,
    showScriptImportModal,
    showBudgetImportModal,
    setNewBudgetIdFunc,
    showShareBudgetModal,
    changeTab,
    renameTab,
    collaboratorsList,
    isBudgetOwner,
    menuPermissions,
    budgetData,
    menuActions,
    hasEditAccess,
    getBudgetsStatus,
    getTabsStatus,
    createBudgetMutation,
    refetchBudgetId,
    refetchBudgets,
    refetchTabs,
    addedContextualSuggestions,
    setAddedContextualSuggestions,
    closeTabsMutation,
    openTabsListInitial,
    selectTabFromDropdown,
    getbudgetDropdownData,
    setActiveBudget,
    handleTabDragEnd,
    recalculateProjectTotal,
    setShowDetailedView,
    swimlanes,
    setSwimlanes,
    showDetailedView,
    showAiInsights,
  } = useHomeScreenContext();

  const { userData, userActivityMutation } = useAppContext();
  const sessionState = useSelector(state => state?.budgetSession);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [editTab, setEditTab] = useState('');
  const swimlaneContainer = useRef();
  const [collaborators, setCollaborators] = useState();
  const [activeBudgetUsers, setActiveBudgetUsers] = useState([]);
  const [budgetCellPresences, setBudgetCellPresences] = useState([]);
  const [newTabName, setNewTabName] = useState('');
  const hasBudgets = openTabsList?.length > 0;
  const hanBudgetsInMyBudgets = budgetList?.length > 0;
  const navigate = useNavigate();
  const budgetHistorySessionStartRef = useRef(new Date().toISOString());
  const mentionableUsers = useMemo(
    () => buildMentionableUsers(collaboratorsList, userData),
    [collaboratorsList, userData]
  );

  // Subscribe to swimlane query so footer gets categories when loaded (e.g. after refresh)
  const { data: swimlaneQueryData } = useBudgetQueries.useSwimlaneQuery({
    enabled: Boolean(hasBudgets && selectedBudget?.budget),
    payload: {
      id: selectedBudget?.budget,
      type: 'swimlane',
    },
  });
  const swimlaneItems = swimlaneQueryData?.data;
  const changeEventsPayload = useMemo(
    () => ({
      budgetId: selectedBudget?.budget,
      since: budgetHistorySessionStartRef.current,
      limit: 75,
    }),
    [selectedBudget?.budget]
  );
  const { data: changeEventsData, isFetching: isFetchingChangeEvents } =
    useBudgetQueries.useBudgetChangeEventsQuery({
      enabled: Boolean(hasBudgets && selectedBudget?.budget),
      payload: changeEventsPayload,
      config: {
        refetchInterval: CHANGE_EVENT_POLL_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
      },
    });
  const budgetChangeEvents = changeEventsData?.data || [];

  const contextualSuggestionsRef = useRef();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [
    contextualCategorySuggestionRequest,
    setContextualCategorySuggestionRequest,
  ] = useState(null);
  const contextualCategorySuggestionRequestId = useRef(0);
  const [isDetailSuggestionMode, setIsDetailSuggestionMode] = useState(false);
  const [hiddenSuggestionKeys, setHiddenSuggestionKeys] = useState({});
  const [categoryColumnFingerprints, setCategoryColumnFingerprints] = useState(
    {}
  );
  const handleCategoryColumnItemsChange = useCallback(
    ({ budgetId, suggestionScopeKey, fingerprint }) => {
      if (!budgetId || !suggestionScopeKey) return;
      const key = `${budgetId}:${suggestionScopeKey}`;
      setCategoryColumnFingerprints(previous =>
        previous[key] === fingerprint
          ? previous
          : { ...previous, [key]: fingerprint }
      );
    },
    []
  );
  const selectedCategorySuggestionTarget = useMemo(() => {
    if (showDetailedView?.show && showDetailedView?.selectedItem?.id) {
      return {
        category: showDetailedView.selectedItem,
        siblingColumnIndex: Number(showDetailedView.level || 0),
      };
    }

    const selectedColumnIndex = (swimlanes || []).reduce(
      (latestIndex, column, index) =>
        column?.selectedItem?.id ? index : latestIndex,
      -1
    );
    if (selectedColumnIndex < 1) return null;

    const selectedCategory = swimlanes[selectedColumnIndex]?.selectedItem;
    return selectedCategory?.id
      ? {
          category: selectedCategory,
          siblingColumnIndex: selectedColumnIndex - 1,
        }
      : null;
  }, [
    showDetailedView?.level,
    showDetailedView?.selectedItem,
    showDetailedView?.show,
    swimlanes,
  ]);
  const isUsingDetailSuggestions = Boolean(
    showDetailedView?.show && isDetailSuggestionMode
  );
  const categorySuggestionScopeKey = useMemo(() => {
    const siblingColumnIndex =
      selectedCategorySuggestionTarget?.siblingColumnIndex;
    if (!Number.isInteger(siblingColumnIndex) || siblingColumnIndex === 0) {
      return 'category:root';
    }

    const parentCategoryId = swimlanes?.[siblingColumnIndex]?.selectedItem?.id;
    return parentCategoryId ? `category:${parentCategoryId}` : 'category:root';
  }, [selectedCategorySuggestionTarget, swimlanes]);
  const suggestionScopeKey = isUsingDetailSuggestions
    ? `detail:${showDetailedView?.selectedItem?.id || ''}`
    : categorySuggestionScopeKey;
  const categoryColumnFingerprint =
    categoryColumnFingerprints[
      `${selectedBudget?.budget || ''}:${categorySuggestionScopeKey}`
    ];

  const getSuggestionKey = useCallback(
    name =>
      `${suggestionScopeKey}:${String(name || '')
        .trim()
        .toLowerCase()}`,
    [suggestionScopeKey]
  );
  const hideSuggestion = useCallback(
    name => {
      const key = getSuggestionKey(name);
      setHiddenSuggestionKeys(previous => ({ ...previous, [key]: true }));
    },
    [getSuggestionKey]
  );
  const restoreSuggestion = useCallback(
    (name, scopeKey) => {
      const key = `${scopeKey || 'category:root'}:${String(name || '')
        .trim()
        .toLowerCase()}`;
      setHiddenSuggestionKeys(previous => {
        if (!previous[key]) return previous;
        const next = { ...previous };
        delete next[key];
        return next;
      });
      setAddedContextualSuggestions(previous => {
        const matchingKey = Object.keys(previous || {}).find(
          suggestionName =>
            suggestionName.trim().toLowerCase() ===
            String(name || '')
              .trim()
              .toLowerCase()
        );
        if (!matchingKey) return previous;
        const next = { ...previous };
        delete next[matchingKey];
        return next;
      });
    },
    [setAddedContextualSuggestions]
  );

  useEffect(() => {
    setIsDetailSuggestionMode(false);
  }, [showDetailedView?.show, showDetailedView?.selectedItem?.id]);
  const activeBudgetUserEmails = useMemo(
    () => new Set(activeBudgetUsers.map(user => user.user_email)),
    [activeBudgetUsers]
  );
  const presenceBreadcrumbs = useMemo(
    () => budgetCellPresences.filter(presence => presence?.context),
    [budgetCellPresences]
  );
  const activePresenceByEmail = useMemo(() => {
    return new Map(
      presenceBreadcrumbs.map(presence => [presence.user_email, presence])
    );
  }, [presenceBreadcrumbs]);

  useEffect(() => {
    const budgetId = selectedBudget?.budget;
    if (!budgetId) {
      setBudgetCellPresences([]);
      return undefined;
    }

    let cancelled = false;
    setBudgetCellPresences([]);

    const fetchBudgetCellPresences = async () => {
      try {
        const response = await API_ENDPOINTS.budgets.get_cell_presence(
          budgetId
        );
        if (!cancelled) {
          setBudgetCellPresences(response?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setBudgetCellPresences([]);
        }
      }
    };

    fetchBudgetCellPresences();
    const intervalId = setInterval(
      fetchBudgetCellPresences,
      PRESENCE_BREADCRUMB_POLL_INTERVAL_MS
    );

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedBudget?.budget]);

  useEffect(() => {
    if (
      !selectedBudget?.id ||
      !selectedBudget?.budget ||
      selectedBudget?.is_optimistic
    ) {
      setActiveBudgetUsers([]);
      return undefined;
    }

    let cancelled = false;
    const refreshActiveBudgetUsers = async () => {
      try {
        const response = await API_ENDPOINTS.tabs.get_active_budget_users(
          selectedBudget.budget
        );
        if (!cancelled) {
          setActiveBudgetUsers(response?.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setActiveBudgetUsers([]);
        }
      }
    };

    refreshActiveBudgetUsers();
    const intervalId = setInterval(refreshActiveBudgetUsers, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    selectedBudget?.id,
    selectedBudget?.budget,
    selectedBudget?.is_optimistic,
  ]);

  useEffect(() => {
    if (
      collaboratorsList?.registered_collaborators?.length ||
      collaboratorsList?.invited_collaborators?.length
    ) {
      let collaboratorUsers =
        !isBudgetOwner() && collaboratorsList?.budget_owner
          ? [
              {
                id: collaboratorsList?.budget_owner?.id,
                name:
                  collaboratorsList?.budget_owner?.name &&
                  collaboratorsList?.budget_owner?.name.length > 1
                    ? collaboratorsList?.budget_owner?.name
                    : collaboratorsList?.budget_owner?.email || '',
                // email: collaboratorsList?.budget_owner?.email,
                email: collaboratorsList?.budget_owner?.email,
                presenceColor: getPresenceColor(
                  collaboratorsList?.budget_owner?.email ||
                    collaboratorsList?.budget_owner?.id
                ),
              },
            ]
          : [];
      collaboratorUsers = [
        ...collaboratorUsers,
        ...(collaboratorsList?.registered_collaborators
          ?.filter(user => user?.collaborator_user_email !== userData?.email)
          ?.map(user => {
            return {
              name: user?.collaborator_name,
              email: user?.collaborator_user_email,
              id: user?.id,
              image: user?.collaborator_profile_image,
              presenceColor: getPresenceColor(
                user?.collaborator_user_email || user?.id
              ),
            };
          }) || []),
        ...(collaboratorsList?.invited_collaborators
          ?.filter(user => user?.collaborator_email !== userData?.email)
          ?.map(user => {
            return {
              name: user?.collaborator_name,
              email: user?.collaborator_email,
              id: user?.id,
              presenceColor: getPresenceColor(
                user?.collaborator_email || user?.id
              ),
            };
          }) || []),
      ];
      setCollaborators(collaboratorUsers);
    } else {
      setCollaborators([]);
    }
  }, [collaboratorsList, userData, isBudgetOwner]);
  useEffect(() => {
    contextualSuggestionsRef?.current?.scrollTo({
      left: scrollPosition,
      top: 0,
      behavior: 'smooth',
    });
  }, [scrollPosition]);

  // Hard recalculation when budget is loaded or changed
  useEffect(() => {
    if (!selectedBudget?.budget) {
      return;
    }

    // Check if swimlane data exists in cache
    const freshSwimlaneData = queryClient.getQueryData([
      'get-swimlanes-in-budget',
      { id: selectedBudget.budget, type: 'swimlane' },
    ]);
    const items = freshSwimlaneData?.data;

    if (items && items.length > 0) {
      // Use setTimeout to ensure query cache is fully populated
      const timeoutId = setTimeout(() => {
        recalculateProjectTotal();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedBudget?.budget,
    budgetData,
    recalculateProjectTotal,
    queryClient,
  ]);

  const handleRenameBudget = async () => {
    if (!newTabName?.length || editTab?.name === newTabName) {
      setNewTabName('');
      disableEditTab();
      return false;
    }

    try {
      const response = await createBudgetMutation.mutateAsync({
        name: newTabName,
        id: editTab?.budget,
      });
      const newBudget = response?.data;
      newBudget?.id && renameTab(newBudget.id, newBudget);
      refetchBudgets?.();
      refetchBudgetId?.();
      // setNewTabName('');
      disableEditTab();
      toast.success(
        `Budget "${editTab?.name}" renamed to "${newTabName}" successfully`,
        toastStyle
      );
    } catch (error) {
      const message =
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.non_field_errors?.[0];
      toast.error(
        message ||
          `An error occurred while renaming budget. Please try again later`,
        toastStyle
      );
    }
  };

  const budgetListMenu = getbudgetDropdownData;

  const handleTabActions = tab => action => {
    if (action === 'close-budget-tab') {
      // closeBudgetsData?.push(selectedBudget);
      handleCloseTabs(tab || selectedBudget);
    } else {
      return menuActions[action]?.();
    }
  };

  const handleCloseTabs = async tab => {
    try {
      const response = await closeTabsMutation.mutateAsync(tab?.id);
      if (Array.isArray(response?.data)) {
        queryClient.setQueryData(['get-open-tabs'], response);
      }
      refetchTabs();
      refetchBudgets();
    } catch (error) {
      console.error('Error closing budget tab:', error);
    }
  };

  const getOverflowBudgetTab = menu => {
    return (
      openTabsListInitial?.find(
        tab => tab?.budget?.toString() === menu?.value?.toString()
      ) || menu
    );
  };

  const handleOverflowBudgetClose = menu => event => {
    event.preventDefault();
    event.stopPropagation();

    const tabToClose = getOverflowBudgetTab(menu);
    if (tabToClose?.id) {
      handleCloseTabs(tabToClose);
    }
  };

  const renderOverflowBudgetMenuItem = menu => {
    const label =
      typeof menu?.label === 'string' && menu?.label?.length > 44
        ? menu?.label?.slice(0, 44)?.concat('..')
        : menu?.label;

    return (
      <div className="budget-overflow-menu-item">
        <span className="budget-overflow-menu-item__label">{label}</span>
        <button
          type="button"
          className="budget-overflow-menu-item__close"
          aria-label={`Close ${menu?.label || 'budget'} tab`}
          title={`Close ${menu?.label || 'budget'} tab`}
          onClick={handleOverflowBudgetClose(menu)}
        >
          x
        </button>
      </div>
    );
  };

  const handleContextualSuggestionClick = item => () => {
    const suggestionName = String(item?.name || item?.label || '').trim();
    if (!suggestionName) return;
    hideSuggestion(suggestionName);

    userActivityMutation.mutate({
      activity_type: userActivity.BUDGET_EXPORTED,
    });
    if (
      item?.kind === 'line_item' &&
      isUsingDetailSuggestions &&
      showDetailedView?.selectedItem?.id
    ) {
      API_ENDPOINTS.budgets
        .create_swimlane_detail_sheet({
          category: showDetailedView.selectedItem.id,
          item: suggestionName,
          quantity: 1,
          multiplier: 1,
        })
        .then(() => refetchBudgetId?.())
        .catch(() => {
          restoreSuggestion(suggestionName, suggestionScopeKey);
          toast.error('Unable to add the suggested line item.', toastStyle);
        });
      return;
    }
    if (selectedCategorySuggestionTarget?.category?.id) {
      contextualCategorySuggestionRequestId.current += 1;
      setContextualCategorySuggestionRequest({
        id: contextualCategorySuggestionRequestId.current,
        name: suggestionName,
        targetCategoryId: selectedCategorySuggestionTarget.category.id,
        targetColumnIndex: selectedCategorySuggestionTarget.siblingColumnIndex,
      });
      return;
    }

    setAddedContextualSuggestions(prev => ({
      ...prev,
      [suggestionName]: true,
    }));
  };

  const handleCreateBudget = () => {
    if (openingCreatedBudgetId) return;
    setNewBudgetIdFunc(true);
    showCreateBudgetModal();
  };
  const renderNewBudgetTab = () => {
    return (
      <div
        className={`budget-tab budget-tab-new-budget ${
          openingCreatedBudgetId ? 'budget-tab-action-disabled' : ''
        }`}
        onClick={() => handleCreateBudget()}
        aria-disabled={Boolean(openingCreatedBudgetId)}
      >
        <AddIcon className="budget-tab__add-icon" />
        New Budget
      </div>
    );
  };

  const renderImportScriptTab = () => {
    return (
      <div
        className={`budget-tab budget-tab-import-script ${
          openingCreatedBudgetId ? 'budget-tab-action-disabled' : ''
        }`}
        onClick={openingCreatedBudgetId ? undefined : showScriptImportModal}
        aria-disabled={Boolean(openingCreatedBudgetId)}
      >
        <Icon name="FilePdfIcon" className="budget-tab__import-icon" />
        Import Script
      </div>
    );
  };

  const renderImportBudgetTab = () => {
    return (
      <div
        className={`budget-tab budget-tab-import-script ${
          openingCreatedBudgetId ? 'budget-tab-action-disabled' : ''
        }`}
        onClick={openingCreatedBudgetId ? undefined : showBudgetImportModal}
        aria-disabled={Boolean(openingCreatedBudgetId)}
      >
        <Icon
          name="ArrowUpFromBracketIcon"
          className="budget-tab__import-icon"
        />
        Import Budget
      </div>
    );
  };

  const enableEditTab = budget => setEditTab(budget);
  const disableEditTab = () => setEditTab(null);

  const handleTabClick = budget => e => {
    if (isBudgetOwner()) {
      stopPropagation(e);
      enableEditTab(budget);
    }
  };

  const handleTabChange = budget => event => {
    if (openingCreatedBudgetId || budget?.is_optimistic) return;

    ///Special guard to stop triggering tab change when tab is already active
    if (
      event.target.closest('.app-dropdown') ||
      event.target.closest('.budget-tab__expand-icon') ||
      event.target.closest('.dropdown-menu')
    ) {
      return;
    }

    changeTab(budget, true);
    budget?.id !== editTab?.id && disableEditTab();
    setActiveBudget(budget?.id);
  };

  const handleTabNameChange = event => {
    const value = event.target.value;
    switch (event.key || event.type) {
      case 'Enter':
      case 'blur':
        event.preventDefault();
        handleRenameBudget();
        break;
      case 'Escape':
      case 'Tab':
        setNewTabName('');
        disableEditTab();
        event.preventDefault();
        break;
      default:
        return setNewTabName(value);
    }
    // window?.location?.reload();
  };

  const loadingState = isInitialBudgetWorkspaceLoading({
    budgetsStatus: getBudgetsStatus,
    tabsStatus: getTabsStatus,
    openTabsList,
  });

  const renderBudgetTab = () => {
    if (loadingState) return null;

    return (
      <DragDropContext onDragEnd={handleTabDragEnd} onDragStart={null}>
        <Droppable droppableId="droppable-tabs" direction="horizontal">
          {provided => (
            <div
              className="budget-tabs"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {hasBudgets ? (
                <>
                  {openTabsList?.map((budget, index) => {
                    const activeClass =
                      selectedBudget?.budget === budget?.budget
                        ? 'budget-tab-active'
                        : '';
                    const enteringClass =
                      enteringBudgetId?.toString() ===
                      budget?.budget?.toString()
                        ? 'budget-tab-entering'
                        : '';
                    return (
                      <Draggable
                        key={budget?.budget}
                        draggableId={String(budget?.budget)}
                        index={index}
                        isDragDisabled={Boolean(
                          openingCreatedBudgetId || budget?.is_optimistic
                        )}
                      >
                        {provide => (
                          <div
                            ref={provide.innerRef}
                            {...provide.draggableProps}
                            {...provide.dragHandleProps}
                            style={getBudgetTabDraggableStyle(
                              provide.draggableProps.style
                            )}
                            className={`budget-tab ${activeClass} ${enteringClass} ${
                              budget?.is_optimistic ? 'budget-tab-opening' : ''
                            }`}
                            key={`budget-tab-${budget?.budget}`}
                            onClick={handleTabChange(budget)}
                          >
                            {editTab?.budget === budget?.budget ? (
                              <FormInput
                                defaultValue={budget?.name}
                                onChange={handleTabNameChange}
                                onKeyDown={handleTabNameChange}
                                onBlur={handleTabNameChange}
                                autoFocus
                              />
                            ) : (
                              <span
                                title={budget?.name}
                                onClick={
                                  activeClass
                                    ? handleTabClick(budget)
                                    : undefined
                                }
                              >
                                {budget?.name}
                              </span>
                            )}
                            {!openingCreatedBudgetId &&
                              !budget?.is_optimistic && (
                                <DropDownMenu
                                  toggleIcon={
                                    <ExpandIcon className="budget-tab__expand-icon" />
                                  }
                                  onChange={handleTabActions(budget)}
                                  menuOptions={
                                    budgetMenu?.filter(
                                      option =>
                                        menuPermissions[option.value] ?? true
                                    ) || []
                                  }
                                />
                              )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {budgetListMenu?.length > 0 && !openingCreatedBudgetId && (
                    <DropDownMenu
                      className="budgets-dropdown"
                      menuOptions={budgetListMenu}
                      onChange={selectTabFromDropdown}
                      align={{ sm: 'end' }}
                      toggleLabel={`+ ${budgetListMenu?.length} more`}
                      renderMenuItemContent={renderOverflowBudgetMenuItem}
                    />
                  )}
                  {renderNewBudgetTab()}
                  {renderImportBudgetTab()}
                  {renderImportScriptTab()}
                </>
              ) : (
                <>
                  {renderNewBudgetTab()}
                  {renderImportBudgetTab()}
                  {renderImportScriptTab()}
                </>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  const findSwimlaneById = useCallback(
    swimlaneId => {
      return (
        swimlaneItems?.find(
          swimlane => swimlane?.id?.toString() === swimlaneId?.toString()
        ) || null
      );
    },
    [swimlaneItems]
  );

  const fetchSwimlaneItems = useCallback(
    async (id, type) => {
      const queryKey = ['get-swimlanes-in-budget', { id, type }];
      const cachedItems = queryClient.getQueryData(queryKey)?.data;
      if (Array.isArray(cachedItems)) return cachedItems;

      const response =
        type === 'line-item'
          ? await API_ENDPOINTS.budgets.get_line_items(id)
          : await API_ENDPOINTS.budgets.get_line_item_children(id);

      queryClient.setQueryData(queryKey, response);
      return response?.data || [];
    },
    [queryClient]
  );

  const openPresenceDetailView = useCallback(
    detailView => {
      setTimeout(() => {
        setShowDetailedView(detailView);
      }, 0);
    },
    [setShowDetailedView]
  );

  const handleCollaboratorPresenceClick = useCallback(
    presence => async event => {
      event?.preventDefault();
      event?.stopPropagation();

      const context = presence?.context;
      if (!selectedBudget?.budget || !context?.swimlane_id) return;

      const swimlane = findSwimlaneById(context.swimlane_id) || {
        id: context.swimlane_id,
        name: context.swimlane_name,
        budget: selectedBudget.budget,
      };
      const rootColumn = {
        type: 'swimlane',
        budgetId: selectedBudget.budget,
      };

      if (!context.line_item_id) {
        setSwimlanes([rootColumn]);
        openPresenceDetailView({
          show: true,
          level: 0,
          selectedItem: swimlane,
        });
        return;
      }

      const pathIds = context.line_item_path_ids?.length
        ? context.line_item_path_ids
        : [context.line_item_id];
      const pathNames = context.line_item_path_names || [];
      const resolvedPath = [];
      let currentItems = [];

      try {
        currentItems = await fetchSwimlaneItems(
          context.swimlane_id,
          'line-item'
        );
      } catch {
        currentItems = [];
      }

      for (let index = 0; index < pathIds.length; index += 1) {
        const itemId = pathIds[index];
        const fallbackItem = {
          id: itemId,
          name:
            pathNames[index] ||
            (itemId?.toString() === context.line_item_id?.toString()
              ? context.line_item_name
              : ''),
          swimlane: context.swimlane_id,
          parent: index > 0 ? pathIds[index - 1] : null,
        };
        const currentItem =
          currentItems?.find(
            item => item?.id?.toString() === itemId?.toString()
          ) || fallbackItem;

        resolvedPath.push(currentItem);

        if (index < pathIds.length - 1) {
          try {
            currentItems = await fetchSwimlaneItems(
              currentItem.id,
              'line-item-children'
            );
          } catch {
            currentItems = [];
          }
        }
      }

      const targetItem = resolvedPath[resolvedPath.length - 1];
      if (!targetItem?.id) return;

      const columns = [
        rootColumn,
        {
          type: 'line-item',
          selectedItem: swimlane,
          swimlane: context.swimlane_id,
        },
        ...resolvedPath.slice(0, -1).map(item => ({
          type: 'line-item-children',
          selectedItem: item,
          swimlane: item.swimlane || context.swimlane_id,
        })),
      ];

      setSwimlanes(columns);
      openPresenceDetailView({
        show: true,
        level: resolvedPath.length,
        selectedItem: targetItem,
      });
    },
    [
      fetchSwimlaneItems,
      findSwimlaneById,
      openPresenceDetailView,
      selectedBudget?.budget,
      setSwimlanes,
    ]
  );

  const renderCollaborators = () => {
    return (
      hasBudgets && (
        <div className="collaborators">
          {collaborators?.slice(0, 4)?.map((user, index) => {
            const isActive = activeBudgetUserEmails.has(user.email);
            const activePresence = activePresenceByEmail.get(user.email);
            const hasName = user.name !== null && user.name !== undefined;
            const hasLongName = hasName && user.name.length > 1;
            const hasEmail = user.email !== null && user.email !== undefined;
            const activeClass = isActive ? 'collaborators-icon-active' : '';
            const jumpClass = activePresence
              ? 'collaborators-icon-clickable'
              : '';
            return (
              <Avatar
                key={`${index}-${user.id}`}
                name={
                  hasLongName && hasEmail
                    ? `${user.name.split(' ').slice(0, 2).join(' ')} (${
                        user?.email
                      })`
                    : hasName && !hasEmail
                    ? user.name
                    : user.email
                }
                wrapperStyle={{
                  '--presence-color': user.presenceColor,
                  background: 'none',
                }}
                imageStyle={{ backgroundColor: user.presenceColor }}
                className={`collaborators-icon ${activeClass} ${jumpClass}`}
                onClick={
                  activePresence
                    ? handleCollaboratorPresenceClick(activePresence)
                    : undefined
                }
                image={
                  user?.image && <img src={user?.image} alt={user?.name} />
                }
              />
            );
          })}
          {collaborators?.length > 4 && (
            <div className="collaborators-expand">
              <DropDownMenu
                className="collaborators-dropdown"
                menuOptions={collaborators
                  ?.slice(4)
                  ?.map(user => ({ label: user.name, value: user.id }))}
              />
            </div>
          )}
          <div className="share-budget-button-wrapper">
            <Button
              label={
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Icon
                    name="SlideshareIcon"
                    style={{ width: '14px', height: '14px' }}
                  />
                  {collaborators?.length > 0 ? 'Sharing' : 'Share Budget'}
                </span>
              }
              type="button"
              className="share-budget-button"
              variant="primary"
              onClick={showShareBudgetModal}
            />
          </div>
        </div>
      )
    );
  };

  const renderNoBudgetScreen = () => {
    if (loadingState) return null;
    if (hasBudgets) return null;

    return (
      <div className="no-budgets-card col-md-4">
        <h3 className="card-header">Lets Get Started!</h3>
        <div className="card-body">
          {hanBudgetsInMyBudgets ? (
            <span>
              You don&apos;t have any budgets open at the moment. Create or open
              a budget to get started.
            </span>
          ) : (
            <span>
              You don&apos;t have any existing budgets at the moment. Create a
              budget to get started.
            </span>
          )}
        </div>
        <div className="card-action">
          <Button
            label="Import Budget"
            type="button"
            className="import-budget-button"
            variant="secondary"
            onClick={showBudgetImportModal}
          />
          <Button
            label="New Budget"
            type="button"
            className="create-budget-button"
            variant="primary"
            onClick={showCreateBudgetModal}
          />
          {hanBudgetsInMyBudgets && (
            <Button
              label="Open Budget"
              type="button"
              className="create-budget-button"
              variant="primary"
              onClick={() => {
                navigate(ALL_ROUTES.BUDGET_DETAILS);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const handleBackClick = () => {
    if (scrollPosition > 0) {
      const position = scrollPosition - 350;
      setScrollPosition(scrollPosition < 0 ? 0 : position);
    }
  };

  const handleFrowardClick = () => {
    const scrollWidth = contextualSuggestionsRef?.current?.scrollWidth;
    if (scrollPosition < scrollWidth) {
      const position = scrollPosition + 350;
      setScrollPosition(scrollPosition > scrollWidth ? scrollWidth : position);
    }
  };

  return (
    <div className="budget-container pe-5">
      <div className="budget-tabs-wrapper">
        {renderBudgetTab()}
        {renderCollaborators()}
      </div>
      <div
        className={[
          'budget-tab-content',
          hasBudgets ? '' : 'no-budgets',
          showDetailedView?.show ? 'budget-tab-content--with-ai-rate-card' : '',
          showDetailedView?.show && showAiInsights
            ? 'budget-tab-content--ai-open'
            : '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={swimlaneContainer}
      >
        {renderNoBudgetScreen()}
        {hasBudgets && selectedBudget?.budget && (
          <SwimlaneLayout
            swimlaneHeight={swimlaneContainer?.current?.offsetHeight}
            budgetId={selectedBudget?.budget}
            presenceBreadcrumbs={presenceBreadcrumbs}
            cellPresences={budgetCellPresences}
            changeEvents={budgetChangeEvents}
            isLoadingChangeEvents={isFetchingChangeEvents}
            contextualCategorySuggestionRequest={
              contextualCategorySuggestionRequest
            }
            onDetailInteraction={() => setIsDetailSuggestionMode(true)}
            onSuggestionDeleted={restoreSuggestion}
            onCategoryColumnItemsChange={handleCategoryColumnItemsChange}
            mentionableUsers={mentionableUsers}
            key={selectedBudget?.budget}
          />
        )}
      </div>
      {hasBudgets && selectedBudget?.budget && (
        <BudgetFooter
          contextualSuggestions={contextualSuggestions}
          addedContextualSuggestions={addedContextualSuggestions}
          swimlaneItems={swimlaneItems}
          scrollPosition={scrollPosition}
          onBackClick={handleBackClick}
          onForwardClick={handleFrowardClick}
          onSuggestionClick={handleContextualSuggestionClick}
          hasEditAccess={hasEditAccess}
          contextualSuggestionsRef={contextualSuggestionsRef}
          budgetData={budgetData}
          budgetId={selectedBudget?.budget}
          categoryId={
            (isUsingDetailSuggestions && showDetailedView?.selectedItem?.id) ||
            selectedCategorySuggestionTarget?.category?.id
          }
          isDetailView={isUsingDetailSuggestions}
          suggestionScopeKey={suggestionScopeKey}
          categoryColumnFingerprint={categoryColumnFingerprint}
          hiddenSuggestionKeys={hiddenSuggestionKeys}
          sessionDelta={
            selectedBudget?.budget && sessionState?.sessionsByBudgetId
              ? sessionState.sessionsByBudgetId[selectedBudget.budget]?.delta ||
                0
              : 0
          }
          currency={selectedBudget?.primary_currency}
          onResetSession={() => {
            const currentTotal = parseFloat(budgetData?.total_cost || 0);
            dispatch(
              startSession({
                budgetId: selectedBudget?.budget,
                initialTotal: currentTotal,
              })
            );
          }}
        />
      )}
      {(loadingState || openingCreatedBudgetId) && (
        <CornerSpinner
          isLoading
          size="sm"
          label={
            openingCreatedBudgetId ? 'Opening new budget' : 'Loading budgets'
          }
        />
      )}
    </div>
  );
};

export default BudgetScreen;
